import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";

export type SelectingType = "source" | "destination";
export type ClearModeType = "source" | "destination" | "";
export type ColorStepType = 1 | 2 | 3 | 4 | 5;

interface IState {
  selectingType: SelectingType;
  source: number[];
  destination: number[];
  level: number;
  visible: boolean;
  zone: number;
  autoApply: boolean;
  applySelecteds: boolean;
  forceUpdate: boolean;
  zonesCount: number;
  showTopNumbers: boolean;
  showHeatMap2Numbers: boolean;
  heatMap2Mode: SelectingType;
  colorStep: ColorStepType;
  clearMode: ClearModeType;
  clearModeUpdate: boolean;
}

const initialState: IState = {
  selectingType: "source",
  source: [],
  destination: [],
  level: 5,
  visible: false,
  zone: 29,
  autoApply: false,
  applySelecteds: false,
  forceUpdate: false,
  zonesCount: 10,
  showTopNumbers: false,
  showHeatMap2Numbers: false,
  heatMap2Mode: "source",
  colorStep: 1,
  clearMode: "",
  clearModeUpdate: false,
};

export const trafficZonesSlice = createSlice({
  name: "trafficZones",
  initialState,
  reducers: {
    selectZone: (state, action: PayloadAction<number[]>) => {
      const zones = action.payload;

      if (state.selectingType === "source") {
        const newValue = zones.filter((i) => !state.destination.includes(i));
        return {
          ...state,
          applySelecteds: false,
          source: newValue,
          destination: state.destination.filter((i) => zones.includes(i)),
        };
      }
      const newValue = zones.filter((i) => !state.source.includes(i));
      return { ...state, applySelecteds: false, source: state.source.filter((i) => zones.includes(i)), destination: newValue };
    },
    swapZones: (state) => {
      return { ...state, source: state.destination, destination: state.source };
    },
    clearDestinationZones: (state) => {
      return { ...state, destination: [], clearMode: "destination" };
    },
    clearSourceZones: (state) => {
      return { ...state, source: [], clearMode: "source" };
    },
    toggleForceUpdate: (state) => {
      return { ...state, forceUpdate: !state.forceUpdate };
    },
    changeLevel: (state, action: PayloadAction<number>) => {
      return { ...state, level: action.payload };
    },
    changeZone: (state, action: PayloadAction<number>) => {
      return { ...state, zone: action.payload };
    },
    changeSelectingType: (state, action: PayloadAction<SelectingType>) => {
      return { ...state, selectingType: action.payload };
    },
    toggleVisibility: (state) => {
      return { ...state, visible: !state.visible };
    },
    changeAutoApply: (state, action: PayloadAction<boolean>) => {
      return { ...state, autoApply: action.payload };
    },
    changeApplySelected: (state, action: PayloadAction<boolean>) => {
      return { ...state, applySelecteds: action.payload };
    },
    toggleClearModeUpdate: (state) => {
      return { ...state, clearModeUpdate: !state.clearModeUpdate };
    },
    changeZonesCount: (state, action: PayloadAction<number>) => {
      return { ...state, zonesCount: action.payload };
    },
    toggleShowTopNumbers: (state) => {
      return { ...state, showTopNumbers: !state.showTopNumbers };
    },
    toggleShowHeatMap2Numbers: (state) => {
      return { ...state, showHeatMap2Numbers: !state.showHeatMap2Numbers };
    },
    changeHeatMap2Mode: (state, action: PayloadAction<SelectingType>) => {
      return { ...state, heatMap2Mode: action.payload };
    },
    changeHeatMap2ColorStep: (state, action: PayloadAction<ColorStepType>) => {
      return { ...state, colorStep: action.payload };
    },
    makeInvisible: (state) => {
      return { ...state, visible: false };
    },
  },
});

export const {
  selectZone,
  clearDestinationZones,
  clearSourceZones,
  changeLevel,
  toggleVisibility,
  changeAutoApply,
  changeApplySelected,
  changeSelectingType,
  toggleForceUpdate,
  swapZones,
  changeZone,
  changeZonesCount,
  toggleShowTopNumbers,
  toggleShowHeatMap2Numbers,
  changeHeatMap2Mode,
  changeHeatMap2ColorStep,
  makeInvisible,
  toggleClearModeUpdate,
} = trafficZonesSlice.actions;

export default trafficZonesSlice.reducer;
